import { useState, useCallback } from 'react';
import { SIDE_TABS, DASHBOARD_TITLE_TABS } from '~/app/constants';

/**
 * useSwitchReturn
 *
 * Owns the switch-return dialog state that was previously class state on FormViewer:
 *
 *   onClickViewReturn()        — open the switch-return confirm dialog
 *   switchReturn(saveReturn)   — close (saving or discarding) and navigate to dashboard
 *
 * State returned for render:
 *   showSwitchReturnDlg — boolean
 *
 * Callers must supply:
 *   returnCloseRef         — ref to returnClose hook (for closeAndSaveReturn / closeReturn)
 *   onSelectSidebarTab     — Redux action
 *   onSetTitleTabProperties — Redux action
 *   onRequestAckNotification — Redux action
 *   onFetchReturnProfileByReturnID — Redux action
 *   notificationIDRef      — ref to current notificationID prop
 *   notificationReturnIDRef — ref to current notificationReturnID prop
 *   SIDE_TABS / DASHBOARD_TITLE_TABS — constants (imported directly)
 */
const useSwitchReturn = ({
  returnCloseRef,
  onSelectSidebarTab,
  onSetTitleTabProperties,
  onRequestAckNotification,
  onFetchReturnProfileByReturnID,
  notificationIDRef,
  notificationReturnIDRef,
}) => {
  const [showSwitchReturnDlg, setShowSwitchReturnDlg] = useState(false);

  const onClickViewReturn = useCallback(() => setShowSwitchReturnDlg(true), []);

  const switchReturn = useCallback(
    saveReturn => {
      if (saveReturn) {
        returnCloseRef.current?.closeAndSaveReturn();
      } else {
        returnCloseRef.current?.closeReturn();
      }
      setShowSwitchReturnDlg(false);
      setTimeout(() => {
        onSelectSidebarTab(SIDE_TABS.DASHBOARD_MAIN);
        onSetTitleTabProperties(
          'Tax Returns',
          true,
          SIDE_TABS.DASHBOARD_MAIN,
          DASHBOARD_TITLE_TABS.LIST_VIEW,
        );
        onSetTitleTabProperties('', false, SIDE_TABS.DASHBOARD_MAIN, DASHBOARD_TITLE_TABS.WIDGETS);
        onSetTitleTabProperties(
          '',
          false,
          SIDE_TABS.DASHBOARD_MAIN,
          DASHBOARD_TITLE_TABS.ACTIVE_RETURN,
        );
      });
      onRequestAckNotification(notificationIDRef.current);
      onFetchReturnProfileByReturnID(notificationReturnIDRef.current);
    },
    [
      returnCloseRef,
      onSelectSidebarTab,
      onSetTitleTabProperties,
      onRequestAckNotification,
      onFetchReturnProfileByReturnID,
      notificationIDRef,
      notificationReturnIDRef,
    ],
  );

  return { showSwitchReturnDlg, setShowSwitchReturnDlg, onClickViewReturn, switchReturn };
};

export default useSwitchReturn;
